import { ReactNode } from 'react'
import IconToggleLabel from '@/authenticated/components/IconToggleLabel'
import ProgramPrescribedAlert from '@/app/components/ProgramPrescribedAlert'
import { ActiveSetting } from '@/types/activeSetting'

/**
 * The charge-schedule "goal" toggles a utility program can prescribe.
 */
export type GoalField =
  | 'money_saving_plan_enabled'
  | 'save_the_planet_plan_enabled'
  | 'solar_surplus_plan_enabled'

type Props<M extends object> = {
  /** Charge-schedule field this toggle binds to, e.g. `money_saving_plan_enabled`. */
  field: GoalField
  /** Human-readable setting name used in the prescribed-alert copy, e.g. "Save Money". */
  settingDisplayName: string
  /**
   * The field's prescription metadata from the active-charge-settings
   * serializer. When `is_program_prescribed` is true the toggle is locked to
   * the program-prescribed `value` and an info alert is shown beneath it; the
   * user can't change it. When `undefined` or not prescribed, the toggle is
   * fully interactive.
   */
  prescription?: ActiveSetting<boolean>

  // --- interactive (non-prescribed) pass-throughs ---
  /** The charge-schedule model the interactive toggle reads/writes. */
  model?: M
  /** Called with the updated model when the interactive toggle changes. */
  onChange?: (model: M) => void
  checked?: boolean
  shouldDebounce?: boolean

  // --- display pass-throughs (apply to both states) ---
  id?: string
  iconComponent?: ReactNode
  title?: string
  subtitle?: string
  newHighlight?: boolean
}

/**
 * A charge-schedule goal toggle that respects utility-program prescriptions.
 *
 * Extends the established `ChargeSettingsCard` convention (a `disabled` +
 * `allowDisabledEnabled` `IconToggleLabel` paired with an info alert) to any
 * prescribable goal. When the program prescribes the setting, the toggle is
 * shown reflecting the prescribed value but is non-interactive, and a
 * `ProgramPrescribedAlert` explains it's required by the program — so the user
 * can't toggle a program-required goal off (or on).
 */
export default function PrescribableGoalToggle<M extends object>({
  field,
  settingDisplayName,
  prescription,
  model,
  onChange,
  checked = false,
  shouldDebounce = true,
  id,
  iconComponent,
  title,
  subtitle,
  newHighlight = false,
}: Props<M>) {
  const isPrescribed = !!prescription?.is_program_prescribed

  if (isPrescribed) {
    const prescribedValue = !!prescription?.value
    return (
      <>
        <IconToggleLabel
          id={id}
          iconComponent={iconComponent}
          title={title}
          subtitle={subtitle}
          newHighlight={newHighlight}
          // Locked to the program-prescribed value, shown as enabled but not
          // interactive — mirrors the `ChargeSettingsCard` `forceChargeControl`
          // toggle. A fresh single-field model drives the displayed state so it
          // always reflects the serializer, independent of the live schedule.
          model={{ [field]: prescribedValue }}
          field={field}
          disabled
          allowDisabledEnabled
        />
        <div className="mt-2">
          <ProgramPrescribedAlert
            settingDisplayName={settingDisplayName}
            requiredDirection={prescribedValue ? 'on' : 'off'}
          />
        </div>
      </>
    )
  }

  return (
    <IconToggleLabel
      id={id}
      iconComponent={iconComponent}
      title={title}
      subtitle={subtitle}
      newHighlight={newHighlight}
      shouldDebounce={shouldDebounce}
      checked={checked}
      model={model}
      field={field}
      onChange={onChange}
    />
  )
}
